import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CONTROL_PREFIX, SESSION_HEADER, READONLY_MESSAGE, ENDED_MESSAGE, readViewAs, writeViewAs, finishViewAs, takeMessage } from '../lib/viewAs';

const AuthContext = createContext(null);

// =============================================================================
// CRITICAL: API URL Configuration
// =============================================================================
// DO NOT change this logic without understanding the deployment architecture:
//
// - PRODUCTION (NODE_ENV='production'): Uses EMPTY string for relative URLs
//   This ensures API calls go to the SAME domain the frontend is served from
//   e.g., https://your-app.emergent.host/api/auth/login
//
// - DEVELOPMENT (NODE_ENV='development'): Uses REACT_APP_BACKEND_URL
//   This allows local development to point to a preview server
//   e.g., https://invoice-reconcile-32.preview.emergentagent.com/api/auth/login
//
// ISSUE HISTORY:
// - March 2026: Production login failures occurred because the frontend was
//   calling the preview URL instead of the production URL. Fixed by using
//   relative URLs in production builds.
//
// If you see "Invalid credentials" in production but the API works via curl,
// check that this logic hasn't been changed!
// =============================================================================
const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// DIA-2 §2.3 — allowed impersonation targets. Must match the backend's
// ROLE_PREVIEW_ALLOWED set in utils/auth.py. `null` (or absent) means
// "no impersonation — the admin uses their real role".
export const ROLE_PREVIEW_TARGETS = ['surveyor', 'operative', 'sub_contractor'];
// SESSIONstorage — not localStorage. Role preview is a temporary testing
// state, not a preference; it should die when the tab closes so an admin
// who forgets they're previewing cannot come back the next morning and
// misread the app's role-restricted behaviour as a bug. It DOES survive
// an accidental F5 mid-test, which is useful.
const IMPERSONATE_SS_KEY = 'lux_impersonate_role';

// Storage helpers wrap try/catch so a locked-down browser environment
// (e.g. private-mode Safari) never crashes the interceptor.
const readImpersonate = () => {
  try { return sessionStorage.getItem(IMPERSONATE_SS_KEY); } catch { return null; }
};
const writeImpersonate = (val) => {
  try {
    if (val) sessionStorage.setItem(IMPERSONATE_SS_KEY, val);
    else     sessionStorage.removeItem(IMPERSONATE_SS_KEY);
  } catch { /* noop */ } // eslint-disable-line no-empty
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('lux_token'));
  const [loading, setLoading] = useState(true);
  // DIA-2 §2.3 — feature flag comes from GET /api/config on mount. When
  // false (default), the impersonate dropdown never renders and the axios
  // interceptor never attaches the header (belt-and-braces: backend already
  // ignores it, but we want zero surface area when flag is off).
  const [rolePreviewEnabled, setRolePreviewEnabled] = useState(false);
  // Currently-active impersonation target. Kept in state AND sessionStorage
  // so an accidental F5 mid-test doesn't drop the preview — but the value
  // dies when the tab closes.
  const [impersonateRole, setImpersonateRoleState] = useState(readImpersonate);
  // Admin "view as user" — server-side session; the token rides on every request header.
  const [viewAs, setViewAs] = useState(readViewAs);

  // realAdminRef — the interceptor uses this to decide whether to attach
  // the X-Impersonate-Role header. Set from /auth/me `_is_real_admin`
  // (which stays true across an impersonation because backend tracks the
  // real admin identity). Cleared on logout.
  const realAdminRef = useRef(false);

  const setImpersonateRole = useCallback((next) => {
    const clean = ROLE_PREVIEW_TARGETS.includes(next) ? next : null;
    setImpersonateRoleState(clean);
    writeImpersonate(clean);
  }, []);

  // Create api instance with useMemo to maintain reference stability
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
    });
    
    // Add request interceptor to always use current token and view context
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('lux_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      // Set view context based on current URL path
      const isPortal = window.location.pathname.startsWith('/portal');
      config.headers['X-View-Context'] = isPortal ? 'portal' : 'admin_dashboard';
      // Admin "view as user" — the session token goes on every call except the
      // control endpoints (which must run as the real admin). Never combined with role preview.
      const va = readViewAs();
      if (va?.token) {
        if (!(config.url || '').startsWith(CONTROL_PREFIX)) config.headers[SESSION_HEADER] = va.token;
        return config;
      }
      // DIA-2 §2.3 — attach the impersonation header ONLY when the stored
      // value is a valid role AND the current user is a genuine admin
      // (`realAdminRef` is set from /auth/me `_is_real_admin`, which the
      // backend maintains across the impersonation itself). Stale / empty
      // / malformed sessionStorage never leaks a header outbound.
      const imp = readImpersonate();
      if (imp && ROLE_PREVIEW_TARGETS.includes(imp) && realAdminRef.current) {
        config.headers['X-Impersonate-Role'] = imp;
      }
      return config;
    });

    // 419 = the view-as session ended server-side (expired / revoked) → back to the admin's own account.
    // 403 read-only = the server refused a write while viewing as someone else.
    let lastReadOnlyToast = 0;
    instance.interceptors.response.use((r) => r, (error) => {
      const status = error.response?.status;
      if (status === 419 && readViewAs()) {
        finishViewAs(ENDED_MESSAGE);
      } else if (status === 403 && error.response?.data?.detail === READONLY_MESSAGE) {
        if (Date.now() - lastReadOnlyToast > 1500) { lastReadOnlyToast = Date.now(); toast.error(READONLY_MESSAGE); }
      }
      return Promise.reject(error);
    });
    
    return instance;
  }, []);

  // Message left behind by an ended view-as session (shown after the reload).
  useEffect(() => {
    const m = takeMessage();
    if (m) setTimeout(() => toast.message(m), 400);
  }, []);

  // DIA-2 §2.3 — one-shot fetch of the public config so we know whether
  // to render the role-preview UI. Failure is silent (feature stays off).
  // If the backend reports the flag is off, wipe any stale sessionStorage
  // impersonation immediately — turning ROLE_PREVIEW_ENABLED off in prod
  // must cleanly end any active preview, not leave a dead header riding
  // on the next requests.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/config`);
        if (cancelled) return;
        const enabled = !!res.data?.role_preview_enabled;
        setRolePreviewEnabled(enabled);
        if (!enabled && readImpersonate()) {
          writeImpersonate(null);
          setImpersonateRoleState(null);
        }
      } catch {
        if (!cancelled) setRolePreviewEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchUser = useCallback(async () => {
    // SKIP /auth/me check while a Google sign-in is in flight.
    // The AuthCallback component owns auth state during the session_id
    // exchange — racing it here causes a 401 → redirect-to-login bounce.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const currentToken = localStorage.getItem('lux_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      // DIA-2 §2.3 — the backend flag stays true across an active
      // impersonation because it remembers the real admin identity.
      realAdminRef.current = !!response.data?._is_real_admin;

      // View-as: keep the stored session in step with the server (edits flag, expiry after an extension).
      const va = readViewAs();
      if (va && response.data?._viewing_as) {
        const next = { ...va, ...response.data._viewing_as, token: va.token };
        writeViewAs(next);
        setViewAs(next);
      } else if (va && !response.data?._viewing_as) {
        writeViewAs(null);
        setViewAs(null);
      }

      const imp = readImpersonate();
      if (imp && !realAdminRef.current) {
        // sessionStorage has a stale impersonation but the JWT belongs
        // to a non-admin — someone tampered with sessionStorage or the
        // account was demoted mid-session. Wipe it.
        writeImpersonate(null);
        setImpersonateRoleState(null);
        setUser(response.data);
        return;
      }
      if (imp && realAdminRef.current && response.data?._previewing_as !== imp) {
        // First call went out without the impersonation header (because
        // realAdminRef was false at request-construct time on mount).
        // Now that we know the real user is admin, re-fetch WITH the
        // header so the returned user object reflects the preview state.
        const followup = await api.get('/auth/me');
        setUser(followup.data);
        return;
      }
      setUser(response.data);
    } catch (error) {
      // 419 = the view-as session ended server-side; the interceptor is already
      // hard-navigating back to the admin's own page — keep their login intact.
      if (error.response?.status === 419) return;
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('lux_token');
      setToken(null);
      setUser(null);
      realAdminRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // DIA-2 §2.3 — when the impersonation target flips (on/off/switch),
  // re-fetch the user so `roles` / `user_type` / all `hasRole`-derived
  // flags update client-side. Skip the very first mount because the
  // effect above already runs fetchUser() and both effects race otherwise.
  const impersonateFirstRunRef = useRef(true);
  useEffect(() => {
    if (impersonateFirstRunRef.current) {
      impersonateFirstRunRef.current = false;
      return;
    }
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateRole]);

  const login = async (email, password) => {
    // DIA-2 §2.3 — fresh login must never inherit a stale impersonation
    // target from sessionStorage.
    writeImpersonate(null);
    setImpersonateRoleState(null);
    writeViewAs(null);
    setViewAs(null);
    realAdminRef.current = false;
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('lux_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  /**
   * Hydrate auth state from an already-issued JWT.
   * Used by /auth/callback after Google sign-in exchanges the session_id
   * with the backend and receives a JWT in response.
   */
  const loginWithToken = (newToken, userData) => {
    writeImpersonate(null);
    setImpersonateRoleState(null);
    writeViewAs(null);
    setViewAs(null);
    realAdminRef.current = false;
    localStorage.setItem('lux_token', newToken);
    setToken(newToken);
    setUser(userData);
    setLoading(false);
  };

  const logout = () => {
    // Logging out while viewing as someone else means "stop viewing" — end the
    // server session and return to the admin's own account instead.
    const va = readViewAs();
    if (va) {
      api.post(`${CONTROL_PREFIX}/${va.id}/end`, { reason: 'exit' }).catch(() => {}).finally(() => finishViewAs(null));
      return;
    }
    localStorage.removeItem('lux_token');
    // DIA-2 §2.3 — never let an impersonation preview survive a logout.
    writeImpersonate(null);
    setImpersonateRoleState(null);
    writeViewAs(null);
    setViewAs(null);
    realAdminRef.current = false;
    setToken(null);
    setUser(null);
  };

  // ---- Admin "view as user" actions -----------------------------------------
  const startViewAs = useCallback(async (userId) => {
    const return_to = window.location.pathname + window.location.search;
    const res = await api.post(`${CONTROL_PREFIX}/start`, { user_id: userId, return_to });
    writeImpersonate(null);
    writeViewAs({ ...res.data.session, token: res.data.token });
    // Full reload so every cached view is rebuilt as the other user; the router sends them to their own home.
    window.location.assign('/projects');
  }, [api]);

  const endViewAs = useCallback(async (reason = 'exit') => {
    const va = readViewAs();
    if (!va) return;
    try { await api.post(`${CONTROL_PREFIX}/${va.id}/end`, { reason }); } catch { /* already ended */ } // eslint-disable-line no-empty
    finishViewAs(reason === 'expired' ? ENDED_MESSAGE : null);
  }, [api]);

  const setViewAsEdits = useCallback(async (enabled, reason) => {
    const va = readViewAs();
    const res = await api.post(`${CONTROL_PREFIX}/${va.id}/allow-edits`, { enabled, reason });
    const next = { ...va, ...res.data, token: va.token };
    writeViewAs(next);
    setViewAs(next);
    return next;
  }, [api]);

  const extendViewAs = useCallback(async () => {
    const va = readViewAs();
    const res = await api.post(`${CONTROL_PREFIX}/${va.id}/extend`);
    const next = { ...va, ...res.data, token: va.token };
    writeViewAs(next);
    setViewAs(next);
    return next;
  }, [api]);

  // Helper function to check if user has a specific role
  // Supports both single role (legacy) and multiple roles (new)
  const hasRole = (role) => {
    if (!user) return false;
    
    // Support for new multi-role system (roles array)
    if (Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    
    // Fallback for legacy single role
    return user.role === role;
  };

  // Helper function to check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    if (!user) return false;
    return roles.some(role => hasRole(role));
  };

  // Role checks - support both legacy single role and new multi-role
  const isAdmin = hasRole('admin');
  const isSurveyor = hasRole('surveyor');
  const isOperative = hasRole('operative');
  
  // Check if user is surveyor only (not admin) - used for edit/delete restrictions
  const isSurveyorOnly = isSurveyor && !isAdmin;
  
  // Legacy role checks for backward compatibility
  const isOffice = hasRole('office') || hasRole('admin'); // office mapped to admin for now
  const isSiteOperative = hasRole('site_operative') || hasRole('operative');
  
  // Permission checks
  const canManageProjects = isAdmin || isSurveyor;
  const canManageTenders = isAdmin || isSurveyor;  // Surveyors can CREATE but not edit/delete
  const canViewSchedule = true; // Everyone can view schedule
  const canEditSchedule = isAdmin || isSurveyor;
  const canViewFinance = isAdmin;
  
  // Edit/Delete permissions (surveyors cannot edit or delete)
  const canEditRecords = isAdmin;  // Only admins can edit
  const canDeleteRecords = isAdmin; // Only admins can delete

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    logout,
    api,
    // DIA-2 §2.3 role preview
    rolePreviewEnabled,
    impersonateRole,
    setImpersonateRole,
    isImpersonating: !!impersonateRole,
    // Admin "view as user" (server-enforced session)
    viewAs,
    isViewingAsUser: !!viewAs,
    viewAsReadOnly: !!viewAs && !viewAs.allow_edits,
    startViewAs,
    endViewAs,
    setViewAsEdits,
    extendViewAs,
    // Settings → Features switches (server-side, admin-controlled)
    features: user?.features || {},
    refreshUser: fetchUser,
    // Role checks
    hasRole,
    hasAnyRole,
    isAdmin,
    isSurveyor,
    isSurveyorOnly,
    isOperative,
    // Legacy role checks
    isOffice,
    isSiteOperative,
    // Permission checks
    canManageProjects,
    canManageTenders,
    canViewSchedule,
    canEditSchedule,
    canViewFinance,
    canEditRecords,
    canDeleteRecords
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
